// Client for the Finanças office endpoints of the lawyer backend. Same
// conventions as the inline fetch calls in the pages; the shared helper lives
// in ./client.

import { ApiError, request } from './client';
import { CaseTenantData } from '../components/CaseTenantBadge';

export { ApiError, errorMessageFrom } from './client';

export interface FinancasOffice {
  id: string;
  name: string;
  code: string | null;
  active: boolean;
}

export interface QueueCase {
  case_id: string;
  client_name: string;
  submitted_at: string | null;
  business_days_waiting: number | null;
  /** The white-label partner the customer bought from; null for a Coepi case. */
  tenant?: CaseTenantData | null;
}

export interface QueueOffice {
  office_id: string | null;
  office_name: string | null;
  waiting: number;
  oldest_waiting_business_days: number | null;
  median_waiting_business_days: number | null;
  completed_last_30d: number;
  median_submitted_to_complete_business_days_30d: number | null;
  cases: QueueCase[];
}

/** Waiting cases per brand over the whole queue, whichever brand is selected. */
export interface QueueBrands {
  coepi_waiting: number;
  partners: { tenantId: string; tenantName: string; waiting: number }[];
}

export interface FinancasQueue {
  generated_at: string;
  offices: QueueOffice[];
  unassigned_waiting: number;
  origin?: string;
  brands?: QueueBrands;
}

/** Which brand's cases: 'all' | 'coepi' | 'partner' | 'tenant:<id>', as on the cases list. */
export type QueueOrigin = string;

export const listFinancasOffices = async (token: string): Promise<FinancasOffice[]> => {
  const data = await request(token, '/financas-offices', { method: 'GET' }, 'Failed to load Finanças offices.');
  return data?.offices || [];
};

// Creates an office. A 409 means an office with that name already exists; the
// backend returns it, so hand it back as if it had just been created and the
// caller simply selects it.
export const createFinancasOffice = async (
  token: string,
  name: string,
  code: string | null
): Promise<FinancasOffice> => {
  try {
    return await request(
      token,
      '/financas-offices',
      { method: 'POST', body: JSON.stringify({ name, code }) },
      'Failed to add the office.'
    );
  } catch (error) {
    if (error instanceof ApiError && error.status === 409 && error.data?.detail?.office) {
      return error.data.detail.office as FinancasOffice;
    }
    throw error;
  }
};

export const setCaseFinancasOffice = async (
  token: string,
  caseId: string,
  officeId: string
): Promise<{ financas_office_id: string; financas_office_name: string }> =>
  request(
    token,
    `/cases/${caseId}/financas-office`,
    { method: 'PUT', body: JSON.stringify({ office_id: officeId }) },
    'Failed to save the Finanças office.'
  );

// Marks a case Submitted and records the office it was filed at in the same
// request, so the case never sits in Submitted without an office.
export const markCaseSubmitted = async (
  token: string,
  caseId: string,
  officeId: string
): Promise<any> =>
  request(
    token,
    `/cases/${caseId}/status`,
    { method: 'PUT', body: JSON.stringify({ status: 'Submitted', financas_office_id: officeId }) },
    'Failed to update the case status.'
  );

export const getFinancasQueue = async (token: string, origin: QueueOrigin = 'all'): Promise<FinancasQueue> =>
  request(
    token,
    origin === 'all' ? '/financas-offices/queue' : `/financas-offices/queue?origin=${encodeURIComponent(origin)}`,
    { method: 'GET' },
    'Failed to load the Finanças queue.'
  );

// The reference Finanças itself uses for a case in the emails it sends back.
// Sending an empty string clears it.
export const setCaseFinancasReference = async (
  token: string,
  caseId: string,
  reference: string
): Promise<{ financas_reference: string; changed: boolean }> =>
  request(
    token,
    `/cases/${caseId}/financas-reference`,
    { method: 'PUT', body: JSON.stringify({ reference }) },
    'Failed to save the Finanças reference.'
  );
